import { siloRepositoryAbi } from "@silo/contract-abi-types";
import { siloRepositoryAddress } from "@silo/contract-addresses";
import {
  ChainPublicClientService,
  makePercent,
  PERCENT_ZERO,
  Percent,
  ProtocolKey,
  ProtocolKeyService,
} from "@silo/types";
import { Effect, pipe } from "effect";
import { getContract } from "viem";
import { ContractsClientError } from "./types";
import { isRevertError } from "../utils/settle-reverts";

export type GetProtocolShareFee = ReturnType<typeof getProtocolShareFee>;
export type GetProtocolShareFeeSuccess =
  Effect.Effect.Success<GetProtocolShareFee>;
export type GetProtocolShareFeeError = Effect.Effect.Error<GetProtocolShareFee>;
export type GetProtocolShareFeeContext =
  Effect.Effect.Context<GetProtocolShareFee>;

const TTL_MS = 60 * 60 * 1000;

interface MemoEntry {
  value: Percent;
  expiresAt: number;
}

const protocolShareFeeMemo = new Map<ProtocolKey, MemoEntry>();

export function getProtocolShareFee() {
  return Effect.gen(function* () {
    const { publicClient } = yield* ChainPublicClientService;
    const protocolKey = yield* ProtocolKeyService;

    const cached = protocolShareFeeMemo.get(protocolKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value;
    }

    const siloRepositoryContract = getContract({
      address: siloRepositoryAddress[protocolKey],
      abi: siloRepositoryAbi,
      client: publicClient,
    });

    const result = yield* pipe(
      Effect.tryPromise({
        try: async () => {
          return siloRepositoryContract.read
            .protocolShareFee()
            .then((value) => ({ ok: true as const, value: makePercent(value) }))
            .catch((error) => {
              if (isRevertError(error)) {
                return { ok: false as const };
              }

              throw error;
            });
        },
        catch: (cause) => new ContractsClientError({ cause }),
      }),
      Effect.tapError((error) =>
        Effect.logError(
          `Failed ❌: getProtocolShareFee protocol=${protocolKey}: ${
            (error as { _tag?: string })._tag ?? String(error)
          }`
        )
      )
    );

    if (!result.ok) {
      yield* Effect.logWarning(
        `Falling back to PERCENT_ZERO protocolShareFee for protocol=${protocolKey} after revert`
      );
      return PERCENT_ZERO;
    }

    protocolShareFeeMemo.set(protocolKey, {
      value: result.value,
      expiresAt: Date.now() + TTL_MS,
    });

    return result.value;
  });
}
